import type {
  ModernizedCourse,
  ModernizedModule,
  ModernizedAssignment,
  ModernizedCourseContentSection,
  ModernizedCategoryScore,
  InaccessibleDocument,
  ModernizedOtherActivity,
} from "../../../../../graphql/generated/graphql";
import {
  ExternalSourceSystem,
  ModernizedActivityStatus,
  ModernizedAuditStatus,
  ModernizedCourseContentSectionType,
  ModernizedQualityIndicator,
  ModernizedScoreCategory,
} from "../../../../../graphql/generated/graphql";

// ---------------------------------------------------------------------------
// UI types consumed by review components (unchanged)
// ---------------------------------------------------------------------------

export type CourseEvaluatorReviewStage = "content-review" | "full-course-review";
export type CourseEvaluatorReviewEffectiveness = "excellent" | "good" | "fair" | "poor";
export type CourseEvaluatorReviewSidebarTag = "new" | "instructional";
export type CourseEvaluatorReviewItemType = "externalLink" | "moduleDescription" | "discussion" | "checklist";
export type CourseEvaluatorReviewDocumentIssueKind = "permissionDenied" | "brokenLink";
export type CourseEvaluatorReviewItemKind = "moduleDescription" | "recommendation" | "assignment" | "suggestedContent";
export type CourseEvaluatorReviewDecision = "accepted" | "rejected" | "edited" | "synced" | null;

export type CourseEvaluatorReviewSidebarMeta =
  | { kind: "tag"; tag: CourseEvaluatorReviewSidebarTag }
  | { kind: "itemType"; itemType: CourseEvaluatorReviewItemType }
  | { kind: "effectiveness"; effectiveness: CourseEvaluatorReviewEffectiveness };

export interface CourseEvaluatorReviewSidebarItemMock {
  id: string;
  title: string;
  kind: CourseEvaluatorReviewItemKind;
  meta?: CourseEvaluatorReviewSidebarMeta[];
}

export interface CourseEvaluatorReviewSidebarGroupMock {
  id: string;
  title: string;
  decided: number;
  total: number;
  defaultExpanded?: boolean;
  items: CourseEvaluatorReviewSidebarItemMock[];
}

export interface CourseEvaluatorReviewCategoryMock {
  name: string;
  score: number;
  maxScore: number;
}

export interface CourseEvaluatorReviewDocumentIssueMock {
  id: string;
  title: string;
  message: string;
  documentIssueKind: CourseEvaluatorReviewDocumentIssueKind;
}

export interface CourseEvaluatorReviewMockData {
  courseId: string;
  courseName: string;
  sourceLabel: string;
  courseCode: string;
  termLabel: string;
  frameworkName: string;
  reviewStage: CourseEvaluatorReviewStage;
  effectiveness: CourseEvaluatorReviewEffectiveness;
  reviewedDateLabel: string;
  score: number;
  maxScore: number;
  reviewedCount: number;
  totalCount: number;
  queuedCount: number;
  skippedCount: number;
  categoryBreakdown: CourseEvaluatorReviewCategoryMock[];
  strengths: string[];
  improvements: string[];
  documentIssues: CourseEvaluatorReviewDocumentIssueMock[];
  sidebarGroups: CourseEvaluatorReviewSidebarGroupMock[];
}

// ---------------------------------------------------------------------------
// Fake ModernizedCourse data (matches real schema)
// ---------------------------------------------------------------------------

type FakeModernizedCourse = Pick<
  ModernizedCourse,
  | "id"
  | "title"
  | "courseCode"
  | "term"
  | "status"
  | "qualityRating"
  | "totalScore"
  | "totalMaxScore"
  | "externalSource"
  | "strengths"
  | "itemsToImprove"
> & {
  categoryScores: Pick<ModernizedCategoryScore, "id" | "category" | "score" | "maxScore">[];
  inaccessibleDocuments: Pick<InaccessibleDocument, "id" | "title" | "errorReason" | "externalUrl">[];
  contentSections: Pick<ModernizedCourseContentSection, "id" | "type" | "isNew">[];
  modules: (Pick<ModernizedModule, "id" | "title" | "status"> & {
    assignments: (Pick<
      ModernizedAssignment,
      "id" | "title" | "status" | "qualityIndicator" | "isNew" | "gradingType"
    > & {
      /** Hint for sidebar display — e.g. "discussion", "instructional" */
      _uiItemType?: CourseEvaluatorReviewItemType;
      _uiTag?: CourseEvaluatorReviewSidebarTag;
    })[];
    otherActivities: Pick<
      ModernizedOtherActivity,
      "id" | "title" | "status" | "qualityIndicator" | "isNew" | "activityType"
    >[];
  })[];
};

const FAKE_MODERNIZED_COURSE_CALC: FakeModernizedCourse = {
  id: "calc-101",
  title: "Calculus I",
  courseCode: "MATH101",
  term: "Spring 2026",
  status: ModernizedAuditStatus.Modernized,
  qualityRating: ModernizedQualityIndicator.Good,
  totalScore: 42,
  totalMaxScore: 50,
  externalSource: ExternalSourceSystem.Brightspace,
  strengths: [
    "Clear module structure with descriptive names that match Brightspace Content hierarchy",
    "Good use of module descriptions for setting learning context",
    "Effective use of diverse Brightspace content types: assignments, quizzes, HTML content, file topics, and external links",
    "Checklist tool used for student progress tracking",
  ],
  itemsToImprove: [
    "Add explicit learning objectives to the Course Overview module description",
    "Include a materials list and instructor availability details in Course Overview",
    "Derivative Applications Project needs a clear description and grading rubric",
    "Add worked examples to the Differentiation Rules HTML content page",
  ],
  categoryScores: [
    { id: "cs-design", category: ModernizedScoreCategory.CourseDesign, score: 11, maxScore: 13 },
    { id: "cs-assessment", category: ModernizedScoreCategory.Assessment, score: 9, maxScore: 13 },
    { id: "cs-engagement", category: ModernizedScoreCategory.Engagement, score: 12, maxScore: 12 },
    { id: "cs-accessibility", category: ModernizedScoreCategory.Accessibility, score: 10, maxScore: 12 },
  ],
  inaccessibleDocuments: [
    {
      id: "lecture-slides",
      title: "Lecture_Slides_Week3.pptx",
      errorReason: "File requires authentication — unable to access",
      externalUrl: null,
    },
    {
      id: "limits-handout",
      title: "Limits_Practice_Handout.pdf",
      errorReason: "Linked document could not be reached during review",
      externalUrl: null,
    },
  ],
  contentSections: [
    { id: "learning-objectives", type: ModernizedCourseContentSectionType.LearningObjectives, isNew: false },
    { id: "course-materials", type: ModernizedCourseContentSectionType.CourseMaterialsList, isNew: false },
    { id: "instructor-availability", type: ModernizedCourseContentSectionType.InstructorAvailability, isNew: false },
  ],
  modules: [
    {
      id: "course-overview",
      title: "Course Overview",
      status: ModernizedActivityStatus.Undecided,
      assignments: [],
      otherActivities: [
        {
          id: "course-overview-description",
          title: "Course Overview",
          status: ModernizedActivityStatus.Undecided,
          qualityIndicator: null,
          isNew: false,
          activityType: "moduleDescription",
        },
      ],
    },
    {
      id: "limits-and-continuity",
      title: "Limits & Continuity",
      status: ModernizedActivityStatus.Undecided,
      assignments: [],
      otherActivities: [
        {
          id: "limits-placeholder",
          title: "Limits & Continuity",
          status: ModernizedActivityStatus.Undecided,
          qualityIndicator: null,
          isNew: false,
          activityType: "moduleDescription",
        },
      ],
    },
    {
      id: "squeeze-theorem",
      title: "Squeeze Theorem Deep Dive",
      status: ModernizedActivityStatus.Undecided,
      assignments: [],
      otherActivities: [
        {
          id: "squeeze-theorem-description",
          title: "Squeeze Theorem Deep Dive",
          status: ModernizedActivityStatus.Undecided,
          qualityIndicator: null,
          isNew: false,
          activityType: "moduleDescription",
        },
      ],
    },
    {
      id: "derivatives",
      title: "Derivatives",
      status: ModernizedActivityStatus.Undecided,
      assignments: [
        {
          id: "derivatives-everyday-life",
          title: "Derivatives in Everyday Life",
          status: ModernizedActivityStatus.Undecided,
          qualityIndicator: ModernizedQualityIndicator.Good,
          isNew: false,
          gradingType: "discussion",
          _uiItemType: "discussion",
        },
        {
          id: "differentiation-rules",
          title: "Differentiation Rules — Study Guide",
          status: ModernizedActivityStatus.Undecided,
          qualityIndicator: ModernizedQualityIndicator.Fair,
          isNew: false,
          gradingType: null,
          _uiTag: "instructional",
        },
      ],
      otherActivities: [
        {
          id: "derivatives-description",
          title: "Derivatives",
          status: ModernizedActivityStatus.Undecided,
          qualityIndicator: null,
          isNew: false,
          activityType: "moduleDescription",
        },
        {
          id: "module-completion-checklist",
          title: "Module 2 Completion Checklist",
          status: ModernizedActivityStatus.Undecided,
          qualityIndicator: null,
          isNew: true,
          activityType: "checklist",
        },
      ],
    },
    {
      id: "derivative-applications",
      title: "Derivative Applications",
      status: ModernizedActivityStatus.Undecided,
      assignments: [],
      otherActivities: [
        {
          id: "derivative-applications-description",
          title: "Derivative Applications",
          status: ModernizedActivityStatus.Undecided,
          qualityIndicator: null,
          isNew: false,
          activityType: "moduleDescription",
        },
      ],
    },
  ],
};

const FAKE_MODERNIZED_COURSES: Record<string, FakeModernizedCourse> = {
  "calc-101": FAKE_MODERNIZED_COURSE_CALC,
  math101: FAKE_MODERNIZED_COURSE_CALC,
  "bio-301": {
    ...FAKE_MODERNIZED_COURSE_CALC,
    id: "bio-301",
    title: "Human Anatomy and Physiology",
    courseCode: "BIO301",
    externalSource: ExternalSourceSystem.Canvas,
  },
  "eng-220": {
    ...FAKE_MODERNIZED_COURSE_CALC,
    id: "eng-220",
    title: "Advanced Composition",
    courseCode: "ENG220",
    externalSource: ExternalSourceSystem.Canvas,
  },
};

// ---------------------------------------------------------------------------
// Mapping: FakeModernizedCourse → CourseEvaluatorReviewMockData
// ---------------------------------------------------------------------------

const SCORE_CATEGORY_LABELS: Record<ModernizedScoreCategory, string> = {
  [ModernizedScoreCategory.CourseDesign]: "Course Design",
  [ModernizedScoreCategory.Assessment]: "Assessments",
  [ModernizedScoreCategory.Engagement]: "Engagement",
  [ModernizedScoreCategory.Accessibility]: "Accessibility",
};

const SOURCE_LABELS: Record<ExternalSourceSystem, string> = {
  [ExternalSourceSystem.Brightspace]: "Brightspace",
  [ExternalSourceSystem.Canvas]: "Canvas",
  [ExternalSourceSystem.Imscc]: "IMSCC",
  [ExternalSourceSystem.Pdf]: "PDF",
  [ExternalSourceSystem.Docx]: "DOCX",
};

const QUALITY_TO_EFFECTIVENESS: Record<ModernizedQualityIndicator, CourseEvaluatorReviewEffectiveness> = {
  [ModernizedQualityIndicator.Excellent]: "excellent",
  [ModernizedQualityIndicator.Good]: "good",
  [ModernizedQualityIndicator.Fair]: "fair",
  [ModernizedQualityIndicator.NeedsRevision]: "poor",
};

const CONTENT_SECTION_TITLES: Record<ModernizedCourseContentSectionType, string> = {
  [ModernizedCourseContentSectionType.LearningObjectives]: "Learning Objectives",
  [ModernizedCourseContentSectionType.CourseMaterialsList]: "Course Materials List",
  [ModernizedCourseContentSectionType.InstructorAvailability]: "Instructor Availability",
  [ModernizedCourseContentSectionType.Other]: "Other",
};

function mapActivityTypeToItemType(activityType: string | null | undefined): CourseEvaluatorReviewItemType {
  switch (activityType) {
    case "moduleDescription":
      return "moduleDescription";
    case "discussion":
      return "discussion";
    case "checklist":
      return "checklist";
    case "externalLink":
      return "externalLink";
    default:
      return "moduleDescription";
  }
}

function mapQualityToEffectiveness(
  quality: ModernizedQualityIndicator | null | undefined
): CourseEvaluatorReviewEffectiveness | undefined {
  if (!quality) return undefined;
  return QUALITY_TO_EFFECTIVENESS[quality];
}

function mapModernizedCourseToReviewData(course: FakeModernizedCourse): CourseEvaluatorReviewMockData {
  // Build course-level content group from contentSections
  const courseLevelItems: CourseEvaluatorReviewSidebarItemMock[] = course.contentSections.map((section) => ({
    id: section.id,
    title: CONTENT_SECTION_TITLES[section.type] ?? section.type,
    kind: "recommendation" as const,
    meta: section.isNew ? [{ kind: "tag" as const, tag: "new" as const }] : undefined,
  }));

  // Add a suggested content item for course-level (matching original mock)
  courseLevelItems.push({
    id: "essence-of-calculus",
    title: "3Blue1Brown: Essence of Calculus Series",
    kind: "suggestedContent",
    meta: [
      { kind: "tag", tag: "new" },
      { kind: "itemType", itemType: "externalLink" },
    ],
  });

  const courseLevelGroup: CourseEvaluatorReviewSidebarGroupMock = {
    id: "course-level-content",
    title: "Course-Level Content",
    decided: 0,
    total: courseLevelItems.length,
    defaultExpanded: true,
    items: courseLevelItems,
  };

  // Build module groups
  const moduleGroups: CourseEvaluatorReviewSidebarGroupMock[] = course.modules.map((mod) => {
    const items: CourseEvaluatorReviewSidebarItemMock[] = [];

    // Module description items from otherActivities with activityType "moduleDescription"
    for (const activity of mod.otherActivities) {
      const meta: CourseEvaluatorReviewSidebarMeta[] = [];
      if (activity.isNew) meta.push({ kind: "tag", tag: "new" });
      meta.push({ kind: "itemType", itemType: mapActivityTypeToItemType(activity.activityType) });
      const effectiveness = mapQualityToEffectiveness(activity.qualityIndicator);
      if (effectiveness) meta.push({ kind: "effectiveness", effectiveness });

      items.push({
        id: activity.id,
        title: activity.title ?? mod.title ?? "",
        kind: activity.activityType === "moduleDescription" ? "moduleDescription" : "suggestedContent",
        meta: meta.length > 0 ? meta : undefined,
      });
    }

    // Assignment items
    for (const assignment of mod.assignments) {
      const meta: CourseEvaluatorReviewSidebarMeta[] = [];
      if (assignment._uiTag) meta.push({ kind: "tag", tag: assignment._uiTag });
      if (assignment.isNew) meta.push({ kind: "tag", tag: "new" });
      if (assignment._uiItemType) meta.push({ kind: "itemType", itemType: assignment._uiItemType });
      const effectiveness = mapQualityToEffectiveness(assignment.qualityIndicator);
      if (effectiveness) meta.push({ kind: "effectiveness", effectiveness });

      items.push({
        id: assignment.id,
        title: assignment.title ?? "",
        kind: "assignment",
        meta: meta.length > 0 ? meta : undefined,
      });
    }

    const totalActivities = mod.otherActivities.length + mod.assignments.length;

    return {
      id: mod.id,
      title: mod.title ?? "",
      decided: 0,
      total: totalActivities,
      defaultExpanded: mod.id === "derivatives" || mod.id === "squeeze-theorem",
      items,
    };
  });

  const allGroups = [courseLevelGroup, ...moduleGroups];
  const totalCount = allGroups.reduce((sum, g) => sum + g.total, 0);

  return {
    courseId: course.id,
    courseName: course.title ?? "",
    sourceLabel: course.externalSource ? SOURCE_LABELS[course.externalSource] : "Unknown",
    courseCode: course.courseCode ?? "",
    termLabel: course.term ?? "",
    frameworkName: "Dallas College Online Teaching Framework 3.1",
    reviewStage: "full-course-review",
    effectiveness: course.qualityRating ? QUALITY_TO_EFFECTIVENESS[course.qualityRating] : "good",
    reviewedDateLabel: "Mar 10, 2026",
    score: course.totalScore ?? 0,
    maxScore: course.totalMaxScore ?? 50,
    reviewedCount: 0,
    totalCount,
    queuedCount: 0,
    skippedCount: 0,
    categoryBreakdown: course.categoryScores.map((cs) => ({
      name: SCORE_CATEGORY_LABELS[cs.category] ?? cs.category,
      score: cs.score ?? 0,
      maxScore: cs.maxScore ?? 0,
    })),
    strengths: [...course.strengths],
    improvements: [...course.itemsToImprove],
    documentIssues: course.inaccessibleDocuments.map((doc) => ({
      id: doc.id,
      title: doc.title ?? "Unknown document",
      message: doc.errorReason ?? "Document could not be accessed",
      documentIssueKind: doc.errorReason?.includes("authentication") ? "permissionDenied" : "brokenLink",
    })),
    sidebarGroups: allGroups,
  };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export const getCourseEvaluatorReviewMockData = (courseId: string | undefined): CourseEvaluatorReviewMockData => {
  const key = courseId ?? "calc-101";
  const fakeCourse = FAKE_MODERNIZED_COURSES[key] ?? { ...FAKE_MODERNIZED_COURSE_CALC, id: key };
  return mapModernizedCourseToReviewData(fakeCourse);
};
